import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Stack,
  Chip,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import PickerFieldTable from '../../../../components/PickerFieldTable';
import { useNotifier } from '../../../../components/Notifier';
import { useTireDamageDataList } from '../../../../stores/tireDamageStore';
import { createTireInspection, updateTireInspection } from '../../../../stores/tireInspectionStore';
import { revalidateTireBatchList } from '../../../../stores/tireBatchStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../utils/i18n';

const buildReasonArgs = (state) => [state.page, state.pageSize, [], state.searchKey, state.searchValue];
const formatReasonOption = (r) => ({ value: r.id, label: r.name, original: r });

export default function TabTireInspectionAddEdit({
  onBack,
  vehicleDetails,
  companyId,
  tireData,
  hmUnit,
  kmUnit,
  date,
  editData,
  queue,
  setQueue,
  refetch
}) {
  const notify = useNotifier();
  const { t } = useLanguage();
  const isEditMode = Boolean(editData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [treadDepth, setTreadDepth] = useState('');
  const [airPressure, setAirPressure] = useState('');
  const [condition, setCondition] = useState('Good');
  const [tireDamageId, setTireDamageId] = useState(null);
  const [inspectorName, setInspectorName] = useState('');
  const [notes, setNotes] = useState('');

  // Backlog State
  const [isBacklog, setIsBacklog] = useState(false);
  const [statusBacklog, setStatusBacklog] = useState('');

  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAbnormalKmHm = useMemo(() => {
    if (kmUnit && hmUnit && Number(hmUnit) > 0) {
      const ratio = Number(kmUnit) / Number(hmUnit);
      if (ratio > 100 || ratio < 1) return true;
    }
    return false;
  }, [kmUnit, hmUnit]);
  // const [axial, setAxial] = useState('');
  // const [radial, setRadial] = useState('');
  // const [depth, setDepth] = useState('');

  // Pre-fill form when editing or adding
  useEffect(() => {
    if (editData) {
      setTreadDepth(editData.tread_depth != null ? String(editData.tread_depth) : '');
      setAirPressure(editData.air_pressure != null ? String(editData.air_pressure) : '');
      setCondition(editData.condition || 'Good');
      setTireDamageId(editData.tire_damage || null);
      setInspectorName(editData.inspector_name || '');
      setNotes(editData.notes || '');
      setStatusBacklog(editData.status_backlog || '');
      if (editData.status_backlog) {
        setIsBacklog(true);
      }
    } else if (tireData) {
      const ap = tireData.raw?.air_pressure ?? tireData.lastInspection?.air_pressure;
      const td = tireData.raw?.tread_depth ?? tireData.lastInspection?.tread_depth;
      setAirPressure(ap != null ? String(ap) : '');
      setTreadDepth(td != null ? String(td) : '');

      // Reset other fields when opening Add form
      setCondition('Good');
      setTireDamageId(null);
      setInspectorName('');
      setNotes('');
      setStatusBacklog('');
      setIsBacklog(false);
    }
  }, [editData, tireData]);

  const damageColumns = [
    {
      field: 'code',
      headerName: t('general.code', 'Code'),
      width: 120,
      renderCell: (row) => row?.original?.code_internal || row?.original?.code || '-'
    },
    { field: 'name', headerName: t('general.name', 'Name'), width: 180, renderCell: (row) => row?.original?.name || '-' },
    { field: 'area', headerName: t('masterPit.area', 'Area'), width: 120, renderCell: (row) => row?.original?.area || '-' },
    { field: 'cause', headerName: t('tireCycle.cause', 'Cause'), width: 120, renderCell: (row) => row?.original?.cause || '-' },
    {
      field: 'recommend_action',
      headerName: t('tireRepairScrap.recommendation', 'Action'),
      width: 120,
      renderCell: (row) => row?.original?.recommend_action || '-'
    },
    {
      field: 'description',
      headerName: t('general.description', 'Description'),
      width: 250,
      renderCell: (row) => row?.original?.description || '-'
    }
  ];

  const reasonPickerParams = usePickerPagination({
    fetchHook: useTireDamageDataList,
    buildArgs: buildReasonArgs,
    formatOption: formatReasonOption
  });

  const tireDamageName = useMemo(() => {
    if (!tireDamageId) return '';
    const opt = reasonPickerParams.options.find((o) => String(o.value) === String(tireDamageId));
    return opt?.label || '';
  }, [tireDamageId, reasonPickerParams.options]);

  const getPayload = () => ({
    company: parseInt(companyId) || vehicleDetails?.company_id || vehicleDetails?.company || 0,
    vehicle: vehicleDetails?.id || tireData?.vehicle || 0,
    position: tireData?.posCode || tireData?.position_code || tireData?.position || '',
    tire_item: tireData.tire_item || tireData.tire_item_id || 0,
    serial_number: tireData.serial_number || '',
    inspection_date: date || dayjs().format('YYYY-MM-DD'),
    tread_depth: parseFloat(treadDepth) || 0,
    air_pressure: parseFloat(airPressure) || 0,
    condition: condition,
    tire_damage: tireDamageId ? parseInt(tireDamageId) : 0,
    tire_damage_name: tireDamageName || '',
    inspector_name: inspectorName,
    notes: notes || '',
    status_backlog: isBacklog ? statusBacklog || '' : '',
    km_unit: parseFloat(kmUnit) || 0,
    hm_unit: parseFloat(hmUnit) || 0
    // axial: isBacklog ? parseFloat(axial) || 0 : 0,
    // radial: isBacklog ? parseFloat(radial) || 0 : 0,
    // depth: isBacklog ? parseFloat(depth) || 0 : 0
  });

  const maxOtd = tireData?.otd || 0;
  const isTreadDepthExceed = maxOtd > 0 && parseFloat(treadDepth) > maxOtd;

  const validateForm = () => {
    setShowErrors(true);
    if (!tireData) {
      notify({ message: t('tireCycle.noTireSelected', 'No tire selected'), severity: 'error' });
      return false;
    }
    if (!treadDepth) {
      notify({ message: t('tireCycle.treadDepthRequired2', 'Tread depth is required'), severity: 'error' });
      return false;
    }
    if (isTreadDepthExceed) {
      notify({
        message: `${t('tireCycle.treadDepthCannotExceedMax', 'Tread depth cannot exceed Max OTD')} (${maxOtd} mm)`,
        severity: 'error'
      });
      return false;
    }
    if (!airPressure) {
      notify({ message: t('tireCycle.airPressureRequired2', 'Air pressure is required'), severity: 'error' });
      return false;
    }
    if (!inspectorName) {
      notify({ message: t('tireCycle.inspectorNameRequired2', 'Inspector name is required'), severity: 'error' });
      return false;
    }
    if (isBacklog && !statusBacklog) {
      notify({
        message: t('tireCycle.priorityRequiredForBacklog', 'Priority is required if Add to Backlog is enabled'),
        severity: 'error'
      });
      return false;
    }
    if (isBacklog && !tireDamageId) {
      notify({
        message: t('tireCycle.tireDamageRequiredForBacklog', 'Tire Damage is required if Add to Backlog is enabled'),
        severity: 'error'
      });
      return false;
    }
    return true;
  };

  const handleInspectNow = () => {
    if (!validateForm()) return;
    if (isAbnormalKmHm) {
      setConfirmOpen(true);
    } else {
      executeInspect();
    }
  };

  const executeInspect = async () => {
    setIsSubmitting(true);
    try {
      const payload = getPayload();

      if (isEditMode) {
        await updateTireInspection(editData.id, payload);
        notify({
          message: t('tireCycle.inspectionUpdatedFor', 'Inspection updated for ') + tireData.serial_number,
          severity: 'success'
        });
      } else {
        await createTireInspection(payload);
        notify({
          message: t('tireCycle.inspectionSavedFor', 'Inspection saved for ') + tireData.serial_number,
          severity: 'success'
        });
      }

      revalidateTireBatchList();

      if (refetch) refetch();
      if (onBack) onBack();
    } catch (error) {
      notify({
        message:
          error.response?.data?.message ||
          t('tireCycle.failedToUpdateSaveInspection', 'Failed to') + (isEditMode ? t('masterMenu.update') : t('buttonTitle.save')),
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          {isEditMode ? t('tireCycle.editInspection', 'Edit Inspection') : t('tireCycle.inspectTire', 'Inspect Tire')}
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          {t('tireCycle.backToList', 'Back to List')}
        </Button>
      </Stack>

      {/* Tire Info Card */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('tireCycle.position', 'Position')}
            </Typography>
            <Chip label={tireData?.posLabel || tireData?.posCode || '-'} size="small" color="primary" />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('tireRepairScrap.serialNumberExport', 'Serial Number')}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {tireData?.serial_number || '-'}
            </Typography>
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('tireRepairScrap.vehicle', 'Vehicle')}
            </Typography>
            <Typography variant="body1">{vehicleDetails?.license_plate || '-'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Inspection Form */}
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('tireRepairScrap.kmUnit', 'KM Unit')}
            type="number"
            fullWidth
            value={kmUnit}
            disabled
            helperText={t('tireCycle.fromVehicleData', 'From vehicle data')}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('tireRepairScrap.hmUnit', 'HM Unit')}
            type="number"
            fullWidth
            value={hmUnit}
            disabled
            helperText={t('tireCycle.fromVehicleData', 'From vehicle data')}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('tireRepairScrap.treadDepthMm', 'Tread Depth (mm)') + '*'}
            type="number"
            fullWidth
            value={treadDepth}
            onChange={(e) => setTreadDepth(e.target.value)}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
            error={isTreadDepthExceed || (showErrors && !treadDepth)}
            helperText={
              isTreadDepthExceed
                ? `${t('tireCycle.valueExceedsMaxOtd', 'Value exceeds Max OTD')} (${maxOtd} mm)`
                : maxOtd > 0
                  ? `${t('tireCycle.maxOtdMm', 'Max OTD:')} ${maxOtd} mm`
                  : ''
            }
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('tireRepairScrap.airPressurePsi', 'Air Pressure (psi)') + '*'}
            type="number"
            fullWidth
            value={airPressure}
            onChange={(e) => setAirPressure(e.target.value)}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label={t('dashboardAsset.condition', 'Condition') + '*'}
            fullWidth
            value={condition}
            onChange={(e) => {
              setCondition(e.target.value);
              if (e.target.value === 'Good') {
                setTireDamageId(null);
                setIsBacklog(false);
                setStatusBacklog('');
              } else if (e.target.value === 'Damaged') {
                setIsBacklog(true);
              }
            }}
          >
            {['Good', 'Damaged'].map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <PickerFieldTable
            label={isBacklog ? t('menu.tire-damage', 'Tire Damage') + '*' : t('menu.tire-damage', 'Tire Damage') + '*'}
            multiSelect={false}
            {...reasonPickerParams}
            options={reasonPickerParams.options}
            columns={damageColumns}
            value={tireDamageId}
            onChange={(val) => setTireDamageId(val)}
            selectedItems={tireDamageId ? reasonPickerParams.options.filter((o) => String(o.value) === String(tireDamageId)) : []}
            getOptionId={(o) => o?.value}
            getOptionLabel={(o) => o?.label}
            dialogTitle={t('tireCycle.selectTireDamage', 'Select Tire Damage')}
            dialogProps={{ maxWidth: 'lg' }}
            disabled={condition === 'Good'}
            error={showErrors && isBacklog && !tireDamageId}
            helperText={
              showErrors && isBacklog && !tireDamageId
                ? t('tireCycle.pleaseSelectTireDamageFirst', 'Please select tire damage first to add to backlog')
                : ''
            }
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <TextField
            label={t('tireRepairScrap.inspector', 'Inspector Name *')}
            fullWidth
            value={inspectorName}
            onChange={(e) => setInspectorName(e.target.value)}
          />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <TextField
            label={t('tireRepairScrap.notes', 'Notes')}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isBacklog}
                onChange={(e) => setIsBacklog(e.target.checked)}
                color="primary"
                disabled={condition !== 'Damaged'}
              />
            }
            label={t('tireCycle.addToBacklog', 'Add To Backlog')}
          />
        </Grid>

        {isBacklog && (
          <>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label={t('tireCycle.priorityRequiredLabel', 'Priority *')}
                fullWidth
                value={statusBacklog}
                onChange={(e) => setStatusBacklog(e.target.value)}
              >
                <MenuItem value="LOW">LOW</MenuItem>
                <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                <MenuItem value="HIGH">HIGH</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </TextField>
            </Grid>
            {/* <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField label="Axial" type="number" fullWidth value={axial} onChange={(e) => setAxial(e.target.value)} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField label="Radial" type="number" fullWidth value={radial} onChange={(e) => setRadial(e.target.value)} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField label="Depth" type="number" fullWidth value={depth} onChange={(e) => setDepth(e.target.value)} />
            </Grid> */}
          </>
        )}

        <Grid item size={{ xs: 12 }}>
          {isEditMode ? (
            <Button variant="contained" color="primary" fullWidth size="large" onClick={handleInspectNow} disabled={isSubmitting}>
              {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('tireCycle.updateInspection', 'Update Inspection')}
            </Button>
          ) : (
            <Button variant="contained" color="primary" fullWidth size="large" onClick={handleInspectNow} disabled={isSubmitting}>
              {t('tireCycle.inspectNow', 'Inspect Now')}
            </Button>
          )}
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => !isSubmitting && setConfirmOpen(false)}>
        <DialogTitle>{t('tireCycle.confirmInspection', 'Confirm Inspection')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('tireCycle.areYouSureUpdateSaveInspection', 'Are you sure you want to') +
              (isEditMode ? t('masterMenu.update', 'update') : t('buttonTitle.save', 'save')) +
              t('inspection.thisInspection', ' this inspection?')}
          </DialogContentText>
          <Typography color="error" variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
            {t(
              'tireCycle.abnormalKmHmWarning',
              'Warning: The inputted KM and HM values appear to be abnormal. Please verify the data is correct before proceeding.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button onClick={executeInspect} variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? t('general.saving', 'Saving...') : t('general.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
